import React, { useMemo, useState } from 'react';

import { BuiltInComponents } from 'craft/constants/schema';

import { ContentThumbnailWithCarousel } from '../components/ContentThumbnailWithCarousel';

export interface DocumentScreenshotProps {
  checkeredBackdrop?: boolean;
  hotspotLimit?: number;
  rootNodeId?: string;
  rootNodeType: BuiltInComponents;
  versionId: string | undefined;
  tooltipNodeIds?: string[];
}

export const DocumentScreenshot = ({
  clientToken,
  checkeredBackdrop,
  hotspotLimit,
  rootNodeId,
  rootNodeType,
  versionId,
  tooltipNodeIds,
}: { clientToken: string } & DocumentScreenshotProps) => {
  const [currentTooltipIndex, setTooltipIndex] = useState<number>(0);

  const rootNodeIds = useMemo(() => {
    if (rootNodeId) {
      return [rootNodeId];
    }

    if (tooltipNodeIds) {
      return tooltipNodeIds;
    }

    return ['root'];
  }, [tooltipNodeIds, rootNodeId]);

  const imgStyles: React.CSSProperties = useMemo(() => {
    const isTooltipOrModal =
      rootNodeType === BuiltInComponents.Tour ||
      rootNodeType === BuiltInComponents.HotspotGroup ||
      rootNodeType === BuiltInComponents.Modal;

    return {
      width: '100%',
      height: '100%',
      objectPosition: isTooltipOrModal ? 'center' : '0 0',
      objectFit: 'cover',
    };
  }, [rootNodeType]);

  const screenshot = useMemo(() => {
    return rootNodeIds.map((nodeId, index) => (
      <img
        key={nodeId}
        style={{
          display: index === currentTooltipIndex ? 'block' : 'none',
          ...imgStyles,
        }}
        alt="content screenshot"
        src={`https://cdn.candu.ai/screenshots/${clientToken}/${versionId}/${nodeId}.png`}
      />
    ));
  }, [clientToken, versionId, rootNodeIds, currentTooltipIndex, imgStyles]);

  if (!versionId) {
    return null;
  }

  return (
    <ContentThumbnailWithCarousel
      checkeredBackdrop={checkeredBackdrop}
      hotspotLimit={hotspotLimit}
      currentTooltip={currentTooltipIndex}
      setTooltip={setTooltipIndex}
      tooltipNodeIds={tooltipNodeIds}
    >
      {screenshot}
    </ContentThumbnailWithCarousel>
  );
};
